/**
 * Utility for building drag-and-drop context
 * Extracts source/destination information for confirmation modal
 */

import i18n from 'i18next';
import { TreeViewBaseItem } from '@mui/x-tree-view/models';
import { BucketType, DragDropContext } from '../types/dragDropTypes';
/**
 * Find an item in the tree by its ID
 */
export const findItemInTree = (
  nodes: TreeViewBaseItem[],
  id: string
): TreeViewBaseItem | null => {
  for (const node of nodes) {
    if (String(node.id) === id) return node;
    if (node.children) {
      const found = findItemInTree(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Extract bucket from item ID
 */
export const getBucketFromItemId = (itemId: string): BucketType | null => {
  if (itemId.includes('unsorted/') || itemId.startsWith('root-unsorted')) return 'unsorted';
  if (itemId.includes('sorted/') || itemId.startsWith('root-sorted')) return 'sorted';
  if (itemId.includes('files/') || itemId.startsWith('root-files')) return 'files';
  if (itemId.includes('consolidated/') || itemId.startsWith('root-consolidated')) return 'consolidated';
  if (itemId.includes('unknown/') || itemId.startsWith('root-unknown')) return 'unknown';
  return null;
};

/**
 * Extract path components from an item ID to build a readable location
 */
const extractPathComponents = (itemId: string): string[] => {
  let path = itemId;
  
  // Handle file format: fileId---bucket/folder-1/folder-2/filename
  if (path.includes('---')) {
    path = path.split('---')[1] || itemId;
  }
  
  // Remove the bucket prefix (sorted/, unsorted/, unknown/, files/, consolidated/)
  path = path.replace(/^(sorted|unsorted|files|consolidated|unknown)\/?/, '');
  
  // Handle root cases (root-sorted, root-unsorted, etc.) - return empty
  if (path.startsWith('root-') || path === '') {
    return [];
  }

  const parts = path.split('/').filter((p) => p && p.length > 0);
  const displayParts: string[] = [];

  for (const part of parts) {
    // Extract folder name from "folder-123-FolderName" format
    const folderMatch = part.match(/^folder-\d+-(.+)$/);
    if (folderMatch && folderMatch[1]) {
      displayParts.push(folderMatch[1]);
      continue;
    }
    
    // Extract from "folder-123" format
    const simpleFolderMatch = part.match(/^folder-\d+$/);
    if (simpleFolderMatch) {
      continue;
    }
    
    // Skip file references, but keep other identifiable paths
    if (!part.startsWith('file-')) {
      displayParts.push(part);
    }
  }

  return displayParts;
};

/**
 * Labels of the folders an item currently sits inside, outermost first.
 *
 * Tree ids are cumulative paths (`sorted/folder-4/folder-5`, or
 * `40---sorted/folder-4/folder-5/file-40` for a file), so every ancestor id is
 * a prefix of the item's own id and can be looked up directly. That gives the
 * real folder names, which `extractPathComponents` cannot do on its own: a
 * bare `folder-<id>` segment carries no name, so it used to be dropped and the
 * whole path collapsed to just the section label.
 *
 * The item's own segment is excluded - a location is where something lives,
 * not the thing itself.
 */
const buildAncestorLabels = (
  itemId: string,
  items: TreeViewBaseItem[],
  includeSelf = false
): string[] => {
  const path = itemId.includes('---') ? itemId.split('---')[1] : itemId;
  if (!path || path.startsWith('root-')) return [];

  const segments = path.split('/');
  const chain = includeSelf ? segments : segments.slice(0, -1);
  const labels: string[] = [];

  // Segment 0 is the bucket name, which is already rendered as the section
  // label, so ancestors start one level in.
  for (let i = 1; i < chain.length; i++) {
    const prefix = chain.slice(0, i + 1).join('/');
    const label = findItemInTree(items, prefix)?.label;
    if (label) {
      labels.push(String(label));
      continue;
    }
    // Fall back to a name embedded in the segment itself, if there is one.
    const embedded = chain[i].match(/^folder-\d+-(.+)$/);
    if (embedded?.[1]) labels.push(embedded[1].replace(/-/g, ' '));
  }

  return labels;
};

/**
 * Build human-readable source/destination location paths
 * Shows full nested structure with container → location format
 */
const buildLocationPath = (itemId: string, bucket?: BucketType, allItems?: TreeViewBaseItem[]): string => {
  const sourceBucket = bucket || getBucketFromItemId(itemId);
  if (!sourceBucket) return i18n.t('Unknown location');

  const pathComponents = allItems?.length
    ? buildAncestorLabels(itemId, allItems)
    : extractPathComponents(itemId);

  const bucketLabel = {
    sorted: i18n.t('Sorted'),
    unsorted: i18n.t('Uploaded Folder'),
    files: i18n.t('Uploaded Files'),
    consolidated: i18n.t('Consolidated Documents'),
    unknown: i18n.t('Unknown'),
  }[sourceBucket];

  if (pathComponents.length === 0) {
    // For root items, still show they're in a specific section
    return bucketLabel;
  }

  // Build full path: "Sorted > Contract Name > Folder"
  return `${bucketLabel} > ${pathComponents.join(' > ')}`;
};

/**
 * Extract contract title and party info from path components
 */
const extractContractInfo = (pathComponents: string[]): { title?: string } => {
  if (pathComponents.length === 0) return {};

  // Contract title is typically the first component
  const title = pathComponents[0];

  return { title };
};

/**
 * Build full destination location path from newPath
 * Looks up folder display names from tree items to show complete hierarchy
 * Always shows the full path from the drop target down to Originals
 */
const buildDestinationLocationFromNewPath = (newPath: string, bucket: BucketType | null, items: TreeViewBaseItem[]): string => {
  if (!bucket || !newPath) return i18n.t('Unknown location');

  const bucketLabel = {
    sorted: i18n.t('Sorted'),
    unsorted: i18n.t('Uploaded Folder'),
    files: i18n.t('Uploaded Files'),
    consolidated: i18n.t('Consolidated Documents'),
    unknown: i18n.t('Unknown'),
  }[bucket];

  // Remove bucket prefix and filename
  let path = newPath.replace(/^(sorted|unsorted|files|consolidated|unknown)\/?/, '');
  path = path.substring(0, path.lastIndexOf('/')) || '';
  
  if (!path) {
    return bucketLabel;
  }

  const parts = path.split('/').filter((p) => p && p.length > 0);
  const displayParts: string[] = [];

  // Build a map of folder/file IDs to their display labels
  const idToLabelMap = new Map<string, string>();
  const buildIdMap = (nodes: TreeViewBaseItem[], parentBucket?: string) => {
    for (const node of nodes) {
      if (node.id && node.label) {
        idToLabelMap.set(String(node.id), String(node.label));
      }
      if (node.children) {
        buildIdMap(node.children, parentBucket);
      }
    }
  };
  buildIdMap(items);

  // Extract display names for each part of the path
  // Prioritize extracting names from folder-ID-Name format first
  for (const part of parts) {
    let displayName: string | null = null;

    // First, check if it's in the format "folder-ID-Name" and extract the name directly
    const folderWithNameMatch = part.match(/^folder-\d+-(.+)$/);
    if (folderWithNameMatch && folderWithNameMatch[1]) {
      // Replace hyphens with spaces for display (but keep the spaces if already present)
      displayName = folderWithNameMatch[1].replace(/-/g, ' ');
    } else {
      // Otherwise, check if it's just "folder-ID" and try to look it up in the map
      const folderId = part.match(/^folder-(\d+)$/)?.[1];
      if (folderId) {
        // Look for this folder ID in our map
        const matchingEntry = Array.from(idToLabelMap.entries()).find(
          ([key]) => key.includes(`folder-${folderId}`)
        );
        if (matchingEntry) {
          displayName = matchingEntry[1];
        }
      }
    }

    // If we got a display name, add it
    if (displayName) {
      displayParts.push(displayName);
    } else if (!part.startsWith('file-')) {
      // For other parts (like regular folder names without ID), keep them as-is
      displayParts.push(part);
    }
    // Skip file-* parts entirely
  }

  if (displayParts.length === 0) {
    return bucketLabel;
  }

  return `${bucketLabel} > ${displayParts.join(' > ')}`;
};

/**
 * Build comprehensive move context for confirmation modal
 * Uses newPath for destination to capture actual full path where file will be placed
 */
export const buildMoveContext = (
  sourceId: string,
  targetId: string,
  itemName: string,
  itemType: 'file' | 'folder',
  items: TreeViewBaseItem[],
  newPath?: string
): DragDropContext => {
  const sourceBucket = getBucketFromItemId(sourceId);
  const targetBucket = getBucketFromItemId(targetId);

  const sourceLocation = buildLocationPath(sourceId, sourceBucket as BucketType, items);
  
  // Build destination location using newPath which contains the actual drop location
  let destLocation = buildLocationPath(targetId, targetBucket as BucketType);
  if (newPath && targetBucket) {
    destLocation = buildDestinationLocationFromNewPath(newPath, targetBucket, items);
  }

  // Contract titles come from the tree so both sides are compared on real
  // folder names. The target itself counts as part of the destination, since
  // that is what the item is being dropped into.
  const sourceComponents = buildAncestorLabels(sourceId, items);
  const destComponents = buildAncestorLabels(targetId, items, true);

  const sourceContractInfo = extractContractInfo(sourceComponents);
  const destContractInfo = extractContractInfo(destComponents);

  return {
    itemId: sourceId,
    itemName,
    itemType,
    sourceBucket: sourceBucket as BucketType,
    targetBucket: targetBucket as BucketType,
    sourceLocation,
    destLocation,
    sourceContractTitle: sourceContractInfo.title,
    destContractTitle: destContractInfo.title,
    blockReason: undefined,
  };
};
